import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { getAccessToken, isCallbackPath, setOnScheduledRefresh } from '../services/auth'
import { api } from '../services/api'
import type { UserInfo } from '../types'

export const useAuthStore = defineStore('auth', () => {
  const isAuthenticated = ref(false)
  const userInfo = ref<UserInfo | null>(null)
  const loading = ref(true)

  // Un usuario de IdP externo no tiene contraseña en Keycloak: la administra su proveedor.
  const isFederated = computed(() => Boolean(userInfo.value?.identity_provider))

  // Tras cada refresh programado del access token, se refresca también el userinfo.
  setOnScheduledRefresh(() => {
    if (isAuthenticated.value) {
      fetchUserInfo().catch((err) => console.error('Error al refrescar userinfo:', err))
    }
  })

  let _initPromise: Promise<void> | null = null

  function initialize(): Promise<void> {
    if (!_initPromise) {
      _initPromise = _doInitialize()
    }
    return _initPromise
  }

  async function _doInitialize(): Promise<void> {
    // Restauración silenciosa de sesión: intenta un refresh contra la cookie HttpOnly.
    // En el callback se omite: la sesión la crea el canje del code y handleLoginCallback
    // hace su propio intento de recuperación, así que aquí solo sumaría un 401.
    if (!isCallbackPath()) {
      const token = await getAccessToken()
      if (token) {
        isAuthenticated.value = true
        await fetchUserInfo()
      }
    }
    loading.value = false
  }

  async function fetchUserInfo(): Promise<void> {
    const realm = import.meta.env.VITE_OIDC_REALM
    userInfo.value = await api.get<UserInfo>(`/login/realms/${realm}/protocol/openid-connect/userinfo`)
  }

  function setAuthenticated(value: boolean): void {
    isAuthenticated.value = value
  }

  function clear(): void {
    isAuthenticated.value = false
    userInfo.value = null
  }

  return { isAuthenticated, userInfo, loading, isFederated, initialize, fetchUserInfo, setAuthenticated, clear }
})
